// Giriş ekranı — oturum yokken tek başına render edilir.
// Form alanları burada tutulur; App'e sadece PanelUser verilir.

import type React from 'react';
import { useState } from 'react';
import type { PanelUser } from '../types';
import { login } from '../api';
import { Icon } from '../ui';

const fieldStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--inset)',
  border: '1px solid var(--border)',
  borderRadius: '10px',
  padding: '12px 14px',
  color: 'var(--text)',
  fontSize: '14px',
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '11px',
  fontWeight: 600,
  color: 'var(--dim)',
  marginBottom: '7px',
  letterSpacing: '.07em',
};

const demoBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '10px 12px',
  background: 'var(--inset)',
  border: '1px solid var(--border)',
  borderRadius: '9px',
  cursor: 'pointer',
  textAlign: 'left',
};

export default function Login({
  onSuccess,
  showDemo,
  title = 'Call Agent Admin',
  subtitle = 'Çağrı merkezi süpervizör paneli',
  icon = 'headset_mic',
}: {
  onSuccess: (user: PanelUser) => void;
  showDemo: boolean;
  /** Ayrı Winback sitesi (web/src/winback-site/) aynı ekranı kendi başlığıyla kullanır. */
  title?: string;
  subtitle?: string;
  icon?: string;
}) {
  const [loginUser, setLoginUser] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  async function doLogin(username: string, password: string) {
    if (loggingIn) return;
    setLoggingIn(true);
    setLoginError('');
    try {
      const user = await login(username, password);
      if (!user) {
        setLoggingIn(false);
        setLoginError('Kullanıcı adı veya şifre hatalı.');
        return;
      }
      // Başarıda loggingIn sıfırlanmaz: App bu bileşeni söker.
      onSuccess(user);
    } catch {
      // Ağ hatası / 500 — buton "Giriş yapılıyor…" da takılı kalmasın.
      setLoggingIn(false);
      setLoginError('Bağlantı hatası. Lütfen tekrar deneyin.');
    }
  }

  function demoLogin(username: string) {
    const p = username === 'admin' ? 'admin' : '1234';
    setLoginUser(username);
    setLoginPass(p);
    doLogin(username, p);
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '-18%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '680px',
          height: '520px',
          background: 'radial-gradient(circle at center,rgba(61,139,255,.10),transparent 68%)',
          pointerEvents: 'none',
        }}
      />
      <div style={{ position: 'relative', width: '100%', maxWidth: '392px', animation: 'caaFade .5s ease both' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '26px' }}>
          <div
            style={{
              width: '46px',
              height: '46px',
              borderRadius: '12px',
              background: 'var(--accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 0 1px rgba(61,139,255,.45),0 10px 26px rgba(61,139,255,.28)',
            }}
          >
            <Icon name={icon} size={27} color="#fff" />
          </div>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 700, letterSpacing: '-.01em', color: 'var(--text)' }}>
              {title}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--dim)' }}>{subtitle}</div>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            doLogin(loginUser, loginPass);
          }}
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '16px',
            padding: '28px',
          }}
        >
          <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)', marginBottom: '4px' }}>Giriş Yap</div>
          <div style={{ fontSize: '13px', color: 'var(--dim)', marginBottom: '22px' }}>
            Devam etmek için hesabınıza giriş yapın.
          </div>

          <label style={labelStyle}>KULLANICI ADI</label>
          <input
            value={loginUser}
            onChange={(e) => setLoginUser(e.target.value)}
            autoComplete="username"
            placeholder="kullanıcı adı"
            style={{ ...fieldStyle, marginBottom: '16px' }}
          />

          <label style={labelStyle}>ŞİFRE</label>
          <input
            value={loginPass}
            onChange={(e) => setLoginPass(e.target.value)}
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            style={fieldStyle}
          />

          {loginError ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '14px',
                padding: '10px 12px',
                background: 'rgba(239,93,93,.1)',
                border: '1px solid rgba(239,93,93,.3)',
                borderRadius: '9px',
                color: 'var(--bad-text)',
                fontSize: '13px',
              }}
            >
              <Icon name="error" size={18} />
              <span>{loginError}</span>
            </div>
          ) : null}

          <button
            type="submit"
            disabled={loggingIn}
            style={{
              width: '100%',
              marginTop: '22px',
              padding: '12px',
              background: 'var(--accent)',
              border: 'none',
              borderRadius: '10px',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {loggingIn ? 'Giriş yapılıyor…' : 'Giriş Yap'}
          </button>
        </form>

        {showDemo ? (
          <div
            style={{
              marginTop: '18px',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              padding: '14px 16px',
            }}
          >
            <div
              style={{
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '.08em',
                color: 'var(--faint)',
                marginBottom: '11px',
              }}
            >
              DEMO HESAPLAR
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button onClick={() => demoLogin('admin')} style={demoBtnStyle}>
                <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>admin</span>
                  <span style={{ fontSize: '11px', color: 'var(--dim)' }}>Yönetici · tüm markalar · Ayarlar dahil</span>
                </span>
                <Icon name="login" size={19} color="var(--faint)" />
              </button>
              <button onClick={() => demoLogin('supervisor1')} style={demoBtnStyle}>
                <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>supervisor1</span>
                  <span style={{ fontSize: '11px', color: 'var(--dim)' }}>Süpervizör · Vegas + Prime</span>
                </span>
                <Icon name="login" size={19} color="var(--faint)" />
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
